/* eslint-disable react/require-default-props */
import React, { useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { Icon, IconButton } from '@picsio/ui';
import {
  CheckIcon, Copy, QuestionTransparent, DownloadCsv,
} from '@picsio/icons';
import { useSelector } from 'react-redux';
import picsioConfig from '../../../../../../../config';
import localization from '../../../../shared/strings';
import { Input, Checkbox } from '../../../../UIComponents';
import Logger from '../../../../services/Logger';
import UpgradePlan from '../../../UpgradePlan';
import isAssetImageChecker from '../../helpers/isAssetImageChecker';
import sendEventToIntercom from '../../../../services/IntercomEventService';
import copyTextToClipboard from '../../../../helpers/copyTextToClipboard';
import { showDialog } from '../../../dialog';
import * as utils from '../../../../shared/utils';
import Tooltip from '../../../Tooltip';
import ua from '../../../../ua';
import showExportCsvDialog from '../../../../helpers/showExportCsvDialog';
import { navigate } from '../../../../helpers/history';

const Cdn = (props) => {
  const [isFocused, setFocused] = useState(false);
  const {
    asset,
    cdnAllowed,
    onChange,
    isVisible,
    inProgress,
    toggleVisibility,
    panelName,
    disabled = false,
  } = props;

  const assetId = asset?._id;
  const { selectedItems, selectedAssetsAggregatedData } = useSelector((state) => state.assets);
  const isPublished = panelName !== 'previewView.right'
    ? selectedAssetsAggregatedData.selectedAssetsWithCdnLinks === selectedItems.length : asset?.cdnLinks?.head;

  const [copied, setCopied] = useState({ shareLink: false, directLink: false });

  const handleChange = (value, id) => {
    const isRestricted = asset?.restrictSettings?.isRestricted;

    function confirm(preivewAssetId) {
      onChange(value, preivewAssetId);

      if (!isVisible) {
        toggleVisibility('detailsAssetPublishToCdnVisibility');
      }
      sendEventToIntercom('upload to cdn from ipanel');
      const isAssetShared = !!value;
      if (isAssetShared) {
        Logger.log('User', 'InfoPanelPublishToCdn', { assetId });
      } else {
        Logger.log('User', 'InfoPanelUnpublishToCdn', { assetId });
      }
    }

    function handleMultipleAssetsPublish(previewAssetId) {
      const notShowDialogBulkCDNPublish = utils.LocalStorage.get('picsio.notShowDialogBulkCDNPublish') || false;
      if (panelName === 'previewView.right') {
        confirm(previewAssetId);
        return;
      }
      if (selectedItems.length > 1 && !notShowDialogBulkCDNPublish) {
        Logger.log('User', 'CDNBulkPublishModal', {
          assets_number: selectedItems.length,
        });
        showDialog({
          title: localization.DIALOGS.BULK_CDN_PUBLISH.title,
          text: localization.DIALOGS.BULK_CDN_PUBLISH.text,
          textBtnCancel: localization.DIALOGS.BULK_CDN_PUBLISH.textBtnCancel,
          textBtnOk: localization.DIALOGS.BULK_CDN_PUBLISH.textBtnOk,
          onOk: ({ checkbox }) => {
            if (checkbox) {
              utils.LocalStorage.set('picsio.notShowDialogBulkCDNPublish', checkbox);
            }
            Logger.log('User', 'CDNBulkPublishModalAction', {
              action: 'confirmed',
              assets_number: selectedItems.length,
            });
            confirm();
          },
          onCancel: () => {
            Logger.log('User', 'CDNBulkPublishModalAction', {
              action: 'cancelled',
              assets_number: selectedItems.length,
            });
          },
          checkbox: {
            value: false,
            label: localization.DIALOGS.BULK_CDN_PUBLISH.checkboxLabel,
          },
        });
      } else {
        confirm();
      }
    }

    function handleMultipleAssetsUnpublish(previewAssetId) {
      const notShowDialogBulkCDNUnpublish = utils.LocalStorage.get('picsio.notShowDialogBulkCDNUnpublish') || false;
      if (panelName === 'previewView.right') {
        confirm(previewAssetId);
        return;
      }
      if (!notShowDialogBulkCDNUnpublish) {
        Logger.log('User', 'CDNUnpublishModal', {
          assets_number: selectedItems.length,
        });
        showDialog({
          title: localization.DIALOGS.BULK_CDN_UNPUBLISH.title,
          text: localization.DIALOGS.BULK_CDN_UNPUBLISH.text,
          textBtnCancel: localization.DIALOGS.BULK_CDN_UNPUBLISH.textBtnCancel,
          textBtnOk: localization.DIALOGS.BULK_CDN_UNPUBLISH.textBtnOk,
          onOk: ({ checkbox }) => {
            if (checkbox) {
              utils.LocalStorage.set('picsio.notShowDialogBulkCDNUnpublish', checkbox);
            }
            confirm();
            Logger.log('User', 'CDNUnpublishModalAction', {
              action: 'confirmed',
              assets_number: selectedItems.length,
            });
          },
          onCancel: () => {
            Logger.log('User', 'CDNUnpublishModalAction', {
              action: 'cancelled',
              assets_number: selectedItems.length,
            });
          },
          checkbox: {
            value: false,
            label: localization.DIALOGS.BULK_CDN_UNPUBLISH.checkboxLabel,
          },
        });
      } else {
        confirm();
      }
    }

    if (isRestricted && value) {
      showDialog({
        title: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.TITLE,
        text: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.TEXT,
        textBtnOk: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.BTN_OK,
        textBtnCancel: localization.DIALOGS.RESTRICTED_ASSET_DETECTED.BTN_CANCEL,
        onOk: () => handleMultipleAssetsPublish(id),
      });
    } else if (value) {
      handleMultipleAssetsPublish(id);
    } else {
      handleMultipleAssetsUnpublish(id);
    }
  };

  const handleTitleClick = () => toggleVisibility('detailsAssetPublishToCdnVisibility');

  const handleInputFocus = () => setFocused(true);

  const handleInputBlur = () => setFocused(false);

  const copyDirectLink = () => {
    Logger.log('User', 'InfoPanelCopySingleCDNLink');

    setCopied((prev) => ({ ...prev, directLink: true }));
    setTimeout(() => {
      setCopied((prev) => ({ ...prev, directLink: false }));
    }, 2000);
    copyTextToClipboard(`${picsioConfig.apiBaseUrl}/public/images/${assetId}/cdn` || '');
  };

  const exportToCSV = () => {
    Logger.log('User', 'ExportToCsvClicked');
    const ASSETS_LIMIT = 65000;
    if (ua.browser.isNotDesktop()) {
      /** Mobile devices is not supported */
      const { title, text, textBtnCancel } = localization.EXPORT_TO_CSV_DIALOG.notAvailable;
      showDialog({
        title,
        text,
        textBtnCancel,
        textBtnOk: null,
      });
      return;
    }
    if (selectedItems.length > ASSETS_LIMIT) {
      /** More than ASSETS_LIMIT */
      const { title, text, textBtnCancel } = localization.EXPORT_TO_CSV_DIALOG.assetsLimit;
      showDialog({
        title,
        text: text(utils.formatNumberWithSpaces(ASSETS_LIMIT)),
        textBtnCancel,
        textBtnOk: null,
      });
      return;
    }
    showExportCsvDialog({ assetIds: selectedItems, initiationLocation: 'CDN input field' });
  };

  const tooltip = useMemo(() => {
    if (copied.directLink) return 'Copied!';
    if (selectedItems.length > 1) {
      return 'Export csv';
    }
    return 'Copy link';
  }, [copied.directLink, selectedItems.length]);

  const inputValue = useMemo(() => {
    if (panelName === 'previewView.right') {
      if (asset?.cdnLinks?.head) {
        return `${picsioConfig.apiBaseUrl}/public/images/${assetId}/cdn` || '';
      }
      return '';
    }
    if (selectedItems.length > 1 && selectedAssetsAggregatedData.selectedAssetsWithCdnLinks > 0) {
      return 'To get all CDN links utilize export CSV functionality';
    }
    if (selectedItems.length === 1 && isPublished) {
      return `${picsioConfig.apiBaseUrl}/public/images/${assetId}/cdn` || '';
    }
    return '';
  }, [asset?.cdnLinks?.head,
    assetId,
    isPublished,
    panelName,
    selectedAssetsAggregatedData.selectedAssetsWithCdnLinks,
    selectedItems.length]);

  const handleAddToPlan = () => {
    Logger.log('User', 'InfoPanelCDNAddToPlan');
    navigate('/billing?tab=overview');
  };

  return (
    <div
      data-qa="details-component-cdn"
      className={cn('detailsPanel__item', { act: isVisible, disabled })}
    >
      <div className="detailsPanel__title">
        <span
          className={cn('detailsPanel__title_text', { withoutTriangle: !cdnAllowed })}
          onClick={handleTitleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleTitleClick()}
        >
          {localization.DETAILS.textCdn}
          <If condition={!cdnAllowed}>
            <UpgradePlan text={localization.BILLING.textAddToPlan} onClick={handleAddToPlan} />
          </If>
        </span>
        <div className="detailsPanel__title_buttons">
          <If condition={!disabled}>
            <Tooltip content={localization.DETAILS.cdnCheckboxText(isPublished)} placement="top">
              <Checkbox
                slide
                inProgress={inProgress}
                value={isPublished}
                onChange={(value) => handleChange(value, assetId)}
                disabled={!cdnAllowed}
              />
            </Tooltip>
          </If>
        </div>
      </div>
      <CSSTransition in={isVisible} timeout={300} classNames="fade">
        <>
          <If condition={isVisible && cdnAllowed}>
            <span className="cdnSubTextWrapper">
              <div className="directLink">{localization.DETAILS.textDirectLink}</div>
              <Tooltip content={localization.DETAILS.tooltipTextCdn} placement="top">
                <Icon
                  id="button-help-share-cdn"
                  color="primary"
                  size="lg"
                >
                  <QuestionTransparent />
                </Icon>
              </Tooltip>
            </span>
            <If condition={selectedItems.length > 1}>
              <div className="cdnSubText">
                {localization.DETAILS.cdnSelectedAssetsCount(selectedAssetsAggregatedData.selectedAssetsWithCdnLinks, selectedItems.length)}
              </div>
            </If>
            <div className={cn('fieldCopyTo', { isFocused })}>
              <div className={cn('fieldCopyToHolderCDN', { notEmpty: inputValue })}>
                <Input
                  value={inputValue}
                  disabled={selectedItems.length > 1 || !inputValue}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="fieldCopyToUrl"
                  placeholder="Direct URL for embedding"
                />
                <If condition={inputValue}>
                  <Tooltip content={tooltip} placement="top">
                    <IconButton
                      onClick={selectedItems.length > 1 ? exportToCSV : copyDirectLink}
                      size={selectedItems.length > 1 ? 'lg' : 'md'}
                      className="fieldCopyToSettings"
                      componentProps={{
                        'data-value': asset?.cdnLinks?.head?.main,
                        'data-testid': 'iconCopyDirectURL',
                      }}
                    >
                      <Choose>
                        <When condition={copied.directLink}>
                          <CheckIcon className="checkIcon" />
                        </When>
                        <When condition={selectedItems.length > 1}>
                          <DownloadCsv />
                        </When>
                        <Otherwise>
                          <Copy />
                        </Otherwise>
                      </Choose>
                    </IconButton>
                  </Tooltip>
                </If>
              </div>
            </div>
          </If>
        </>
      </CSSTransition>
    </div>
  );
};

Cdn.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  inProgress: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  cdnAllowed: PropTypes.bool.isRequired,
  asset: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    fileExtension: PropTypes.string,
    singleSharingSettings: PropTypes.shape({
      isShared: PropTypes.bool,
    }),
    restrictSettings: PropTypes.shape({
      isRestricted: PropTypes.bool,
    }),
    cdnLinks: PropTypes.shape({
      head: PropTypes.shape({}),
    }),
  }).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  panelName: PropTypes.string,
};

export default memo(Cdn);
